/** @flow */

import React, { Component, type Element } from "react";
import Step from "./step";

type OwnProps = $Exact<{
  data: Array<Object>,
  onChange?: (data?: Array<Object>, valid?: boolean) => any,
  defaultStep?: number,
  onSubmit?: (data?: Array<Object>) => any,
  isReadOnly?: boolean,
  children: Array<Element<typeof Step>>,
}>;

type State = $Exact<{
  currentStep: number,
  rawData: Array<
    $Exact<{
      data: Object,
      valid: boolean,
    }>,
  >,
  stepsCount: number,
}>;

function arrayReplace(state: Array<any>, index: number, newItem: Object): Array<any> {
  return [...state.slice(0, index), newItem, ...state.slice(index + 1, state.length)];
}

class NonLinearWizard extends Component<OwnProps, State> {
  state = {
    currentStep: this.props.defaultStep || 1,
    rawData: this.props.data || [{}],
    stepsCount: this.props.children.length,
  };

  onToggleStepEdit = (step: number) => () =>
    this.setState({
      currentStep: step + 1,
    });

  onStepChange = (step: number) => (values?: Object = {}, valid?: boolean = true) =>
    this.setState(
      {
        rawData: arrayReplace(this.state.rawData, step, {
          data: values,
          valid,
        }),
      },
      () => {
        const { onChange } = this.props;
        onChange && onChange(this.getRawValues(), this.isValid());
      },
    );

  isValid() {
    return (
      this.state.rawData.length === this.props.children.length &&
      this.state.rawData.every(part => part.valid)
    );
  }

  getValidValues() {
    return this.state.rawData.filter(part => part.valid).map(part => part.data);
  }

  getRawValues() {
    return this.state.rawData.map(part => part.data);
  }

  handleStepSubmit = (step: number) => (values?: Object, valid?: boolean = true) =>
    this.setState(
      {
        rawData: arrayReplace(this.state.rawData, step, {
          data: values || this.state.rawData[step].data,
          valid: valid !== undefined ? valid : this.state.rawData[step].valid,
        }),
        currentStep: this.state.stepsCount > step ? step + 2 : step + 1,
      },
      () => {
        if (this.state.stepsCount === step + 1) {
          if (this.isValid()) {
            const { onSubmit } = this.props;
            onSubmit && onSubmit(this.getValidValues()); // Strange Flow issue without destruction
            return;
          } else if (this.state.rawData.length === this.props.children.length) {
            this.setState({
              currentStep: this.state.rawData.findIndex(part => !part.valid) + 1,
            });
          }
        }
        const { onChange } = this.props;
        onChange && onChange(this.getRawValues(), this.isValid());
      },
    );

  render() {
    return (
      <div>
        {this.props.children.map(
          (element, index) =>
            !element.props.hidden &&
            React.cloneElement(element, {
              onChange: this.onStepChange(index),
              editable: this.state.currentStep - 1 === index,
              handleSubmit: this.handleStepSubmit(index),
              isTouched: this.state.rawData[index] !== undefined,
              data: this.state.rawData[index] && this.state.rawData[index].data,
              isValid: this.state.rawData[index] && this.state.rawData[index].valid,
              onToggleEdit: this.onToggleStepEdit(index),
              isCollapsable: false,
              currentStep: this.state.currentStep,
              step: index,
              key: index,
            }),
        )}
      </div>
    );
  }
}

export default NonLinearWizard;
